import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ClientTestimonials = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Sarah Chen",
      title: "IT Director",
      company: "TechCorp Manufacturing",
      industry: "Manufacturing",
      employees: 150,
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face",
      quote: `The transformation was remarkable. We went from constant connectivity issues to rock-solid performance. Our productivity increased by 35% in the first quarter alone, and our remote collaboration capabilities are now world-class.`,
      metrics: {
        speedIncrease: "50x faster",
        downtimeReduction: "95% less",
        productivityGain: "35% increase",
        roiPeriod: "8 months"
      },
      businessImpact: "Enabled real-time manufacturing data analytics and reduced production delays by 40%"
    },
    {
      id: 2,
      name: "Michael Rodriguez",
      title: "Operations Manager",
      company: "Regional Law Firm",
      industry: "Legal Services",
      employees: 75,
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      quote: `Xtreem Fibre Solutions delivered exactly what they promised. Zero disruption during installation, and the performance improvements were immediate. Our client video conferences are crystal clear, and document sharing is instantaneous.`,
      metrics: {
        speedIncrease: "100x faster",
        downtimeReduction: "90% less",
        productivityGain: "28% increase",
        roiPeriod: "12 months"
      },
      businessImpact: "Improved client service delivery and enabled secure remote work for all attorneys"
    },
    {
      id: 3,
      name: "Dr. Amanda Foster",
      title: "Chief Technology Officer",
      company: "Healthcare Partners",
      industry: "Healthcare",
      employees: 200,
      image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=400&fit=crop&crop=face",
      quote: `The reliability improvement has been game-changing for our patient care systems. We've had zero network-related downtime in 18 months, and our telemedicine capabilities have expanded dramatically.`,
      metrics: {
        speedIncrease: "75x faster",
        downtimeReduction: "99% less",
        productivityGain: "42% increase",
        roiPeriod: "6 months"
      },
      businessImpact: "Enhanced patient care through reliable telemedicine and real-time medical record access"
    },
    {
      id: 4,
      name: "James Thompson",
      title: "Facilities Director",
      company: "Financial Services Corp",
      industry: "Financial Services",
      employees: 300,
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
      quote: `Security and compliance were our biggest concerns. Xtreem Fibre Solutions not only met all our regulatory requirements but exceeded them. The network performance has given us a competitive advantage in high-frequency trading.`,
      metrics: {
        speedIncrease: "200x faster",
        downtimeReduction: "98% less",
        productivityGain: "55% increase",
        roiPeriod: "4 months"
      },
      businessImpact: "Achieved sub-millisecond latency for trading systems and enhanced data security compliance"
    },
    {
      id: 5,
      name: "Lisa Park",
      title: "IT Manager",
      company: "Creative Design Agency",
      industry: "Creative Services",
      employees: 45,
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face",
      quote: `Working with large video files used to be a nightmare. Now our creative team can collaborate on 4K projects in real-time. Client presentations are seamless, and our project delivery time has improved significantly.`,
      metrics: {
        speedIncrease: "80x faster",
        downtimeReduction: "85% less",
        productivityGain: "45% increase",
        roiPeriod: "10 months"
      },
      businessImpact: "Enabled real-time 4K video collaboration and reduced project delivery time by 30%"
    }
  ];

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials?.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
  };

  const currentTestimonial = testimonials?.[activeTestimonial];

  return (
    <div className="bg-surface rounded-xl p-8 mb-12">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-text-primary mb-4">
          Client Success Stories
        </h2>
        <p className="text-text-secondary text-lg">
          Real businesses, real results. See how fiber conversion transformed their operations.
        </p>
      </div>
      <div className="max-w-6xl mx-auto">
        {/* Main Testimonial */}
        <div className="bg-white rounded-xl shadow-brand-moderate p-8 mb-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Client Info */}
            <div className="text-center lg:text-left">
              <div className="relative w-32 h-32 mx-auto lg:mx-0 mb-4">
                <Image
                  src={currentTestimonial?.image}
                  alt={currentTestimonial?.name}
                  className="w-full h-full rounded-full object-cover"
                />
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                  <Icon name="Quote" size={16} color="white" />
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-text-primary mb-1">
                {currentTestimonial?.name}
              </h3>
              <p className="text-primary font-medium mb-1">
                {currentTestimonial?.title}
              </p>
              <p className="text-text-secondary text-sm mb-2">
                {currentTestimonial?.company}
              </p>
              <div className="flex items-center justify-center lg:justify-start text-sm text-text-secondary">
                <Icon name="Building" size={14} className="mr-2" />
                <span>{currentTestimonial?.industry} • {currentTestimonial?.employees} employees</span>
              </div>
            </div>

            {/* Quote */}
            <div className="lg:col-span-2">
              <blockquote className="text-lg text-text-primary leading-relaxed mb-6">
                "{currentTestimonial?.quote}"
              </blockquote>
              
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-accent mb-2">Business Impact:</h4>
                <p className="text-text-primary text-sm">
                  {currentTestimonial?.businessImpact}
                </p>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-xl font-bold text-blue-600">
                    {currentTestimonial?.metrics?.speedIncrease}
                  </div>
                  <div className="text-xs text-blue-700">Speed Increase</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-600">
                    {currentTestimonial?.metrics?.downtimeReduction}
                  </div>
                  <div className="text-xs text-green-700">Downtime Reduction</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-xl font-bold text-purple-600">
                    {currentTestimonial?.metrics?.productivityGain}
                  </div>
                  <div className="text-xs text-purple-700">Productivity Gain</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <div className="text-xl font-bold text-orange-600">
                    {currentTestimonial?.metrics?.roiPeriod}
                  </div>
                  <div className="text-xs text-orange-700">ROI Payback</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={prevTestimonial}
            className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-brand-subtle hover:shadow-brand-moderate transition-all duration-200"
          >
            <Icon name="ChevronLeft" size={20} className="text-primary" />
            <span className="text-primary font-medium">Previous</span>
          </button>

          {/* Dots Indicator */}
          <div className="flex space-x-2">
            {testimonials?.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === activeTestimonial ? 'bg-primary' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextTestimonial}
            className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-brand-subtle hover:shadow-brand-moderate transition-all duration-200"
          >
            <span className="text-primary font-medium">Next</span>
            <Icon name="ChevronRight" size={20} className="text-primary" />
          </button>
        </div>

        {/* Industry Overview */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg p-6 shadow-brand-subtle text-center">
            <Icon name="TrendingUp" size={32} className="mx-auto mb-4 text-accent" />
            <h3 className="font-bold text-text-primary mb-2">Average ROI</h3>
            <div className="text-2xl font-bold text-accent mb-1">340%</div>
            <p className="text-sm text-text-secondary">First year return on investment</p>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-brand-subtle text-center">
            <Icon name="Clock" size={32} className="mx-auto mb-4 text-primary" />
            <h3 className="font-bold text-text-primary mb-2">Payback Period</h3>
            <div className="text-2xl font-bold text-primary mb-1">8 months</div>
            <p className="text-sm text-text-secondary">Average time to break even</p>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-brand-subtle text-center">
            <Icon name="Users" size={32} className="mx-auto mb-4 text-secondary" />
            <h3 className="font-bold text-text-primary mb-2">Client Satisfaction</h3>
            <div className="text-2xl font-bold text-secondary mb-1">98%</div>
            <p className="text-sm text-text-secondary">Would recommend to others</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientTestimonials;